/**
 * Remembers which tours a user has already seen, per browser.
 * Keyed by username so shared machines don't suppress tours for other users.
 */
const STORAGE_KEY = "elelem_tours_seen";

const readAll = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode, quota): tours simply auto-start again.
  }
};

const userKey = (username) => username || "_anonymous";

// Set localStorage "elelem_tours_disabled" to "true" to stop tours from
// auto-starting (E2E tests, demos). Tours can still be replayed manually.
const DISABLE_KEY = "elelem_tours_disabled";

const autoStartDisabled = () => {
  try {
    return window.localStorage.getItem(DISABLE_KEY) === "true";
  } catch {
    return false;
  }
};

export const hasSeenTour = (username, tourId) =>
  autoStartDisabled() || Boolean(readAll()[userKey(username)]?.[tourId]);

export const markTourSeen = (username, tourId) => {
  const all = readAll();
  const key = userKey(username);
  all[key] = { ...(all[key] || {}), [tourId]: new Date().toISOString() };
  writeAll(all);
};

const PROGRESS_KEY = "elelem_tours_progress";

const readProgressAll = () => {
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeProgressAll = (value) => {
  try {
    window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable
  }
};

export const getTourProgress = (username, tourId) => {
  if (!tourId) return null;
  const user = userKey(username);
  const userProgress = readProgressAll()[user];
  return userProgress?.[tourId] ?? null;
};

export const setTourProgress = (username, tourId, progress) => {
  if (!tourId) return;
  const all = readProgressAll();
  const user = userKey(username);
  all[user] = {
    ...(all[user] || {}),
    [tourId]: typeof progress === "number" ? { index: progress } : progress,
  };
  writeProgressAll(all);
};

export const clearTourProgress = (username, tourId) => {
  if (!tourId) return;
  const all = readProgressAll();
  const user = userKey(username);
  if (all[user] && tourId in all[user]) {
    delete all[user][tourId];
    writeProgressAll(all);
  }
};

export const TOUR_STORAGE_KEY = STORAGE_KEY;
export const TOURS_DISABLED_KEY = DISABLE_KEY;
export const TOUR_PROGRESS_KEY = PROGRESS_KEY;
